import { LitElement } from 'lit';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzDialog, GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { BasePopUpData, CreateTaskRequest, TaskTarget } from '../types';
import { FooterButton, NetworkPopUpFooterContentWebComponent } from './NetworkPopUpFooterContentWebComponent';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { translateFn } from '../../../utils/translator.helper';
import { ToastActionButtonMode, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { buildHash, NavigationHelper } from '@nirvana/shared/navigation';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';

export enum NetworkPopUpEvents {
  SET_CONFIRM_BUTTON = 'setConfirmButton',
  SET_CLOSE_BUTTON = 'setCloseButton',
  FORCE_CLOSE_POPUP = 'forceClosePopup',
}

export abstract class NetworkBasePopUp<PopUpContent extends LitElement | undefined, PopUpSpecifics = any> {
  protected popUpContent: PopUpContent;
  protected popUpFooterContent: NetworkPopUpFooterContentWebComponent;
  protected data: BasePopUpData<PopUpSpecifics>;
  protected popUpFooterButtons: Partial<FooterButton>[];
  protected errorMessagePrefix = translateFn('Task could not be created. ');
  protected transformToTaskTarget = false;
  protected customEventNameAfterConfirm: string | null = null;
  protected extModelReference: string | null = null;
  protected shouldCloseDialogAfterConfirm = true;
  public dependencies: any;

  protected abstract title: string;
  protected abstract extraDialogConfigs: Partial<GzDialog>;
  protected abstract customSetup(): void;
  protected abstract handleConfirmButtonClicked(event: CustomEvent): Promise<void>;

  constructor(data: BasePopUpData<PopUpSpecifics>) {
    if (!data) {
      this.data = {};
      return;
    }

    this.data = data;
  }

  private baseSetup() {
    if (!this.popUpFooterContent) {
      this.popUpFooterContent = new NetworkPopUpFooterContentWebComponent();

      if (!isEmpty(this.popUpFooterButtons)) {
        this.popUpFooterButtons.map((button: Partial<FooterButton>) => {
          this.popUpFooterContent.updateFooterButton(button.id as string, button);
        });
      }
    }

    const container = document.getElementById('mainPanel') as HTMLElement;

    const dialog = GzDialogWebComponent.buildDialogElement(
      {
        ...this.extraDialogConfigs,
        headline: this.title,
        bodyContent: this.popUpContent,
        isOpen: true,
        footerContent: this.popUpFooterContent,
      },
      container
    );

    this.popUpFooterContent.addEventListener(GzDialogEvents.CANCEL, (event: CustomEvent): void => {
      this.handleCancelButtonClicked(event);
      dialog.close();
    });

    this.popUpFooterContent.addEventListener(GzDialogEvents.CONFIRM, async (event: CustomEvent): Promise<void> => {
      await this.handleConfirmButtonClicked(event);
      if (this.customEventNameAfterConfirm !== null) {
        if (!this.data.parentElementRef) {
          console.error('Could not fire event because the parent element reference is undefined.');
        } else {
          this.data.parentElementRef.dispatchEvent(
            new CustomEvent(this.customEventNameAfterConfirm, {
              bubbles: true,
              composed: true,
              detail: this.getCustomEventDetail(),
            })
          );
        }
      }

      if (this.shouldCloseDialogAfterConfirm) {
        dialog.close();
      }
    });

    if (!this.popUpContent) {
      return;
    }

    this.popUpContent.addEventListener(NetworkPopUpEvents.SET_CONFIRM_BUTTON, (event: CustomEvent): void => {
      const value = event.detail.value;
      if (value === undefined) {
        return;
      }

      this.popUpFooterContent.setConfirmButtonDisabled(value);
    });

    this.popUpContent.addEventListener(NetworkPopUpEvents.SET_CLOSE_BUTTON, (event: CustomEvent): void => {
      const value = event.detail.value;
      if (value === undefined) {
        return;
      }

      this.popUpFooterContent.setCloseButtonDisabled(value);
    });

    this.popUpContent.addEventListener(NetworkPopUpEvents.FORCE_CLOSE_POPUP, (event: CustomEvent): void => {
      dialog.close();
    });
  }

  protected handleCancelButtonClicked(event: CustomEvent) {}

  public getContent(): PopUpContent {
    if (this.extModelReference) {
      this.data.targets = this.transformProtectedEntitiesToExtModel();
    }
    this.customSetup();
    // only for old popups, return immediately, no need to run baseSetup()
    if (this.data.shouldOnlyExecuteCustomSetup) {
      return this.popUpContent;
    }

    this.baseSetup();
    return this.popUpContent;
  }

  protected showTaskCreatedToast(): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: translateFn('Task created successfully. Check the status here: '),
      hasAction: true,
      actionButtonText: translateFn('Tasks'),
      actionCallback: buildHash('/tasks'),
      severity: ToastSeverity.SUCCESS,
      actionButtonMode: ToastActionButtonMode.LINK,
    });
  }

  /**
   * Creates a toast with a custom message. Default severity is SUCCESS.
   */
  protected showCustomToast(message: string, severity = ToastSeverity.SUCCESS) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: severity,
    });
  }

  protected showErrorToastNotification(message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.ERROR,
    });
  }

  protected async createTask<TaskSpecifics = undefined>(payload: CreateTaskRequest<TaskSpecifics>): Promise<void> {
    try {
      await this.createTaskBase<TaskSpecifics>(payload);
      this.showTaskCreatedToast();
    } catch (e) {
      this.showErrorToastNotification(translateFn('The task could not be created. ') + e.err?.message);
    }
  }

  protected async createTaskBase<TaskSpecifics>(payload: CreateTaskRequest<TaskSpecifics>) {
    if (this.transformToTaskTarget) {
      payload.settings.targets = this.transformProtectedEntitiesToTargetEntities();
    }

    payload.settings.source = Constants.Tasks.Endpoints.TASK_SOURCE.NETWORK;

    await ConsoleRequest.createRequest({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: 'Tasks',
      method: 'createTasks',
      payload: payload,
    });
  }

  protected transformProtectedEntitiesToExtModel(): Array<any> {
    if (!this.data.targets) {
      throw new Error('No targets provided');
    }

    if (!this.extModelReference) {
      throw new Error('No ext model reference set');
    }

    return this.data.targets.map((entity: ProtectedEntity) => Ext.create(this.extModelReference, entity));
  }

  protected transformProtectedEntitiesToTargetEntities(): Array<TaskTarget> {
    if (!this.data.targets) {
      throw new Error('No targets provided');
    }

    return this.data.targets.map((entity: ProtectedEntity) => {
      if (!entity.company) {
        throw new Error(`Missing company from entity ${entity._id}`);
      }

      return {
        protectedEntityId: entity._id,
        name: Translator.translateProtectedEntityNameByType(
          entity.name,
          entity.type,
          Constants.ProductTypes.GRAVITY_ZONE_CLOUD
        ),
        type: entity.type,
        path: entity.path,
        isSva: entity.specifics.isSva ?? false,
        company: entity.company,
      };
    });
  }

  /**
   * This function should be overwritten when custom detail is needed.
   * @protected
   */
  protected getCustomEventDetail(): any {
    return undefined;
  }
}
